import { useState, useRef, useEffect } from "react";
import classNames from "classnames";

interface TabOption {
  id: string | number;
  label: string;
}

interface TabsProps {
  options: TabOption[];
  defaultSelected?: string | number;
  onSelect?: (id: string | number) => void;
  variant?: "primary" | "secondary";
  selectedTab?: string | number;
  setSelectedTab?: (id: string | number) => void;
}

const Tabs = ({
  options,
  defaultSelected,
  onSelect,
  variant = "primary",
  selectedTab,
  setSelectedTab,
}: TabsProps) => {
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    if (variant === "secondary") {
      const selectedIndex = options.findIndex((opt) => opt.id === selectedTab);
      const selectedElement = tabRefs.current[selectedIndex];

      if (selectedElement) {
        const left = selectedElement.offsetLeft;
        const width = selectedElement.offsetWidth;
        setSliderStyle({ left, width });
      }
    }
  }, [selectedTab, variant, options]);

  const handleTabClick = (id: string | number) => {
    setSelectedTab?.(id);
    onSelect?.(id);
  };

  return (
    <div
      className={classNames("flex flex-shrink-0 w-fit", {
        "gap-2": variant === "primary",
        "border border-gray-200 rounded-full bg-white relative shadow-inner":
          variant === "secondary",
      })}
    >
      {variant === "secondary" && (
        <div
          className="absolute rounded-full transition-all duration-200 ease-in-out bg-[#002F36]"
          style={{
            left: sliderStyle.left,
            width: sliderStyle.width,
            height: "100%",
          }}
        />
      )}
      {options.map((option, index) => (
        <div
          key={option.id}
          ref={(el) => (tabRefs.current[index] = el)}
          className={classNames(
            "px-3 sm:px-4 py-1.5 sm:py-2 cursor-pointer transition-all duration-200 font-semibold relative text-sm sm:text-base",
            {
              "uppercase rounded-[10px] border border-gray-200":
                variant === "primary",
              "rounded-full z-10": variant === "secondary",
            },
            {
              "bg-[#27D17E] text-white border-[#27D17E]":
                selectedTab === option.id && variant === "primary",
              "text-white":
                selectedTab === option.id && variant === "secondary",
              "hover:bg-gray-100":
                selectedTab !== option.id && variant === "primary",
              "text-gray-600":
                selectedTab !== option.id && variant === "secondary",
            }
          )}
          onClick={() => handleTabClick(option.id)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
};

export default Tabs;
